// Ranks the catalog for a query and builds the suggestions of the header search. Everything runs
// in the browser over the list from catalog.js, so results update on every keystroke.
import seo from '../seo/seoConfig.json';
import bikeCollections from '../content/bikeCollections.json';
import { SERIES_CONFIG } from '../config/menuConfig';
import { fold, slug, tokenize, alternatives, wordMatch } from './text';
import { CATEGORY_WORDS, ACCESSORY_WORDS } from './catalog';

const FIELD_WEIGHT = { name: 10, category: 10, meta: 6, spec: 3 };
const MATCH_WEIGHT = { 3: 1, 2: 0.7, 1: 0.4 }; // exact, prefix, close spelling
const KIND_BONUS = { ebike: 0.3, bike: 0.3, navidad: 0.2, accessory: 0 };

// Best score of one query word against one item: its own spelling may be a close match; a
// synonym only counts written out (or as a prefix) and never weighs more than a category word,
// so 'niña' puts the Christmas bikes before a series that happens to be called "Kids"
function wordScore(words, token) {
    const alts = alternatives(token);
    let best = 0;
    for (const field of Object.keys(FIELD_WEIGHT)) {
        if (FIELD_WEIGHT[field] <= best) continue;
        for (const word of words[field] || []) {
            for (const alt of alts) {
                const match = wordMatch(alt, word);
                if (!match || (alt !== token && match === 1)) continue;
                const weight = alt === token ? FIELD_WEIGHT[field] : Math.min(FIELD_WEIGHT[field], FIELD_WEIGHT.meta) * 0.9;
                const score = weight * MATCH_WEIGHT[match];
                if (score > best) best = score;
            }
        }
    }
    return best;
}

function phraseBonus(item, phrase, tokens) {
    const name = fold(item.name);
    if (name === phrase) return 20;
    if (name.startsWith(phrase)) return 10;
    if (tokens.length > 1 && name.includes(phrase)) return 6;
    return 0;
}

// Items that match every word of the query, best first. When nothing matches all the words,
// `partial` is true and the list holds the items that match some of them.
// Single letters ('e mtb') would only ever match themselves, so they are left out of the query
const queryTokens = (query, max) => tokenize(query).filter((t) => t.length > 1 || /\d/.test(t)).slice(0, max);

export function search(items, query) {
    const tokens = queryTokens(query, 8);
    if (!tokens.length) return { items: items.slice(), partial: false, tokens };
    const phrase = fold(query).replace(/\s+/g, ' ').trim();

    const rank = (requireAll) => {
        const scored = [];
        items.forEach((item) => {
            let total = 0;
            let matched = 0;
            for (const token of tokens) {
                const score = wordScore(item.words, token);
                if (score) {
                    matched += 1;
                    total += score;
                } else if (requireAll) {
                    return;
                }
            }
            if (!matched) return;
            total += phraseBonus(item, phrase, tokens) + KIND_BONUS[item.kind] + (item.popular ? 1 : 0) + (item.price ? 0.5 : 0) + (item.image ? 0.5 : 0);
            scored.push({ item, score: total, matched });
        });
        scored.sort((a, b) => b.matched - a.matched || b.score - a.score || a.item.name.localeCompare(b.item.name));
        return scored;
    };

    let scored = rank(true);
    let partial = false;
    if (!scored.length && tokens.length > 1) {
        scored = rank(false);
        partial = true;
    }
    return { items: scored.map((s) => ({ ...s.item, score: s.score })), partial, tokens };
}

// Pages the search can send people to besides products: categories, collections, series and help
function destinations() {
    const list = [
        { name: 'Bicicletas de Navidad para niños', url: '/navidad', words: 'navidad niños niñas regalo reyes nochebuena aro 12 16 20 raleigh julen carolina princess bmx balance' },
        { name: 'Todas las e-bikes', url: '/ebikes', words: 'ebike eléctrica eléctricas motor raymon bicicleta' },
        { name: 'Todas las bicicletas Raymon', url: '/catalogo', words: 'bicicleta bicicletas raymon catálogo' },
        { name: 'Bicicletas análogas', url: '/bicicletas-analogas', words: 'bicicleta bicicletas análoga análogas convencional mecánica raymon' },
        { name: 'Toda la tienda', url: '/tienda', words: 'tienda productos todo catálogo' },
        { name: 'Todos los accesorios', url: '/accesorios', words: 'accesorios accesorio repuestos' },
        { name: 'Agendar mantenimiento en el taller', url: '/agendar-mantenimiento', words: 'taller mantenimiento servicio reparación tune up mecánico ajuste cita' },
        { name: 'Bicicletas para niños: cómo elegir la talla', url: '/bicicletas-para-ninos-santo-domingo', words: 'niños niñas talla edad guía medida estatura aro' }
    ];
    Object.entries(seo.categories).forEach(([id, category]) => {
        list.push({ name: `Bicicletas ${category.name}`, url: `/category/${category.slug}`, words: `${category.name} ${CATEGORY_WORDS[id] || ''} bicicleta raymon` });
    });
    bikeCollections.forEach((c) => {
        list.push({ name: `Bicicletas ${c.short.toLowerCase().startsWith('aro') ? c.short.toLowerCase() : c.short}`, url: `/bicicletas/${c.slug}`, words: `${c.short} ${c.h1 || ''} bicicleta` });
    });
    Object.values(SERIES_CONFIG).forEach((s) => {
        list.push({ name: `Raymon ${s.label}`, url: s.link, words: `${s.label} ${s.sub} serie raymon bicicleta` });
    });
    Object.entries(ACCESSORY_WORDS).forEach(([category, words]) => {
        list.push({ name: category, url: `/tienda?tipo=accessory&cat=${slug(category)}`, words: `${category} ${words} accesorio` });
    });
    return list.map((d) => ({ ...d, nameWords: tokenize(d.name), allWords: tokenize(`${d.name} ${d.words}`) }));
}

let DESTINATIONS = null;

export function suggestLinks(query, limit = 4) {
    const tokens = queryTokens(query, 6);
    if (!tokens.length) return [];
    if (!DESTINATIONS) DESTINATIONS = destinations();
    const scored = [];
    DESTINATIONS.forEach((d) => {
        let total = 0;
        for (const token of tokens) {
            const score = wordScore({ name: d.nameWords, meta: d.allWords, spec: [] }, token);
            if (!score) return;
            total += score;
        }
        scored.push({ ...d, score: total });
    });
    return scored.sort((a, b) => b.score - a.score).slice(0, limit);
}

// What the header panel shows while typing
export function suggest(items, query, limit = 6) {
    const { items: results, partial } = search(items, query);
    return {
        products: results.slice(0, limit),
        links: suggestLinks(query),
        total: partial ? 0 : results.length,
        partial
    };
}

export const POPULAR_SEARCHES = ['E-bikes', 'Bicis de Navidad', 'Cascos', 'Luces', 'Guantes', 'Gravel', 'Niños', 'Trekking'];
